import { Slide, SlideChannel } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID, StainType } from 'interfaces/stainType';
import { filter, find, findIndex, forEach, fromPairs, isEmpty, keys, map, some, sortBy, toLower, trim } from 'lodash';
import { ChannelModality, getEffectiveChannelModality } from 'utils/multiplex/channelModality';

export const MISSING_MARKER_NAME_LABEL = 'Missing marker name';
export const RNA_PROBE_LABEL = 'RNA probe';

export type MultiplexChannelKind = 'dna' | 'rna-probe' | 'marker' | 'missing';

export interface MultiplexChannelNamingRow {
  channelId: string;
  sourceName?: string;
  markerId?: string;
  markerLabel: string;
  kind: MultiplexChannelKind;
}

const getAssignedMarkerId = (value?: string | null) => {
  const normalizedValue = trim(value || '');
  return normalizedValue || undefined;
};

const normalizeStainLookupValue = (value?: string | null) => toLower(trim(value || ''));

const findStainType = (value: string | undefined, stainTypeOptions: StainType[]) => {
  const normalizedValue = normalizeStainLookupValue(value);
  if (!normalizedValue) {
    return undefined;
  }

  // Stain display names never decide a match; IDs and curated aliases do.
  return find(
    stainTypeOptions,
    (stainType) =>
      normalizeStainLookupValue(stainType.id) === normalizedValue ||
      some(stainType.aliases, (alias) => normalizeStainLookupValue(alias) === normalizedValue)
  );
};

// A channel without a marker type has no modality; channel names never decide it.
const getChannelModality = ({
  markerId,
  stainTypeOptions = [],
  channel,
}: {
  markerId?: string;
  stainTypeOptions?: StainType[];
  channel?: SlideChannel | null;
}): ChannelModality | undefined =>
  markerId ? getEffectiveChannelModality({ markerType: markerId, stainTypeOptions, channel }) : undefined;

export const isDnaChannel = (params: {
  markerId?: string;
  stainTypeOptions?: StainType[];
  channel?: SlideChannel | null;
}) => getChannelModality(params) === 'dna';

export const isRnaProbeChannel = (params: {
  markerId?: string;
  stainTypeOptions?: StainType[];
  channel?: SlideChannel | null;
}) => getChannelModality(params) === 'rna';

// Recorded slide channels are the source of truth; marker assignments on other ids are stale.
export const getMultiplexChannelIds = (slide?: Slide | null) =>
  sortBy(isEmpty(slide?.channels) ? keys(slide?.channelMarkerTypes || {}) : keys(slide?.channels), (channelId) =>
    Number(channelId)
  );

export const getMultiplexChannelNamingRows = (
  slide?: Slide | null,
  stainTypeOptions: StainType[] = []
): MultiplexChannelNamingRow[] => {
  const availableStainTypes = stainTypeOptions || [];
  const channelIds = getMultiplexChannelIds(slide);
  const rows: MultiplexChannelNamingRow[] = map(channelIds, (channelId) => {
    const sourceName = trim(slide?.channels?.[channelId]?.name || '') || undefined;
    const markerId = getAssignedMarkerId(slide?.channelMarkerTypes?.[channelId]);
    const channel = slide?.channels?.[channelId];
    const stainType = findStainType(markerId, availableStainTypes);
    const modality = getChannelModality({ markerId, stainTypeOptions: availableStainTypes, channel });
    const isDna = modality === 'dna';
    const isRnaProbe = modality === 'rna';

    if (!markerId) {
      return {
        channelId,
        sourceName,
        markerLabel: MISSING_MARKER_NAME_LABEL,
        kind: 'missing',
      } satisfies MultiplexChannelNamingRow;
    }

    return {
      channelId,
      sourceName,
      markerId,
      markerLabel: stainType?.displayName || markerId,
      kind: isDna ? 'dna' : isRnaProbe ? 'rna-probe' : 'marker',
    } satisfies MultiplexChannelNamingRow;
  });

  const dnaChannelIndex = findIndex(rows, { kind: 'dna' });
  if (dnaChannelIndex <= 0) {
    return rows;
  }

  return [rows[dnaChannelIndex], ...filter(rows, (_row, index) => index !== dnaChannelIndex)];
};

export const getChannelSecondaryLabel = (row: MultiplexChannelNamingRow) => {
  if (row.kind === 'rna-probe') {
    return RNA_PROBE_LABEL;
  }

  const normalizedSourceName = toLower(row.sourceName || '');
  if (
    !normalizedSourceName ||
    normalizedSourceName === toLower(row.markerLabel) ||
    normalizedSourceName === toLower(row.markerId || '')
  ) {
    return undefined;
  }

  return row.sourceName;
};

export const getNamedNonDnaChannelCount = (rows: MultiplexChannelNamingRow[]) =>
  filter(rows, (row) => row.kind !== 'dna' && row.kind !== 'missing').length;

export const getMissingNonDnaChannelCount = (rows: MultiplexChannelNamingRow[]) =>
  filter(rows, { kind: 'missing' }).length;

export const getMultiplexMarkersChipCopy = (rows: MultiplexChannelNamingRow[]) => {
  const namedNonDnaCount = getNamedNonDnaChannelCount(rows);
  const missingNonDnaCount = getMissingNonDnaChannelCount(rows);
  const channelCount = rows.length;

  if (missingNonDnaCount > 0 && namedNonDnaCount === 0) {
    return {
      chipLabel: 'Define multiplex panels',
      popoverTitle: 'Define multiplex panels',
      isIncomplete: true,
      opensMarkerNamingForm: true,
      guidance: 'Name the non-DNA channels so this cohort can run normalization or RNA-probe ranges.',
    };
  }

  if (missingNonDnaCount > 0) {
    const namedTotal = namedNonDnaCount + missingNonDnaCount;
    return {
      chipLabel: `${namedNonDnaCount} of ${namedTotal} named`,
      popoverTitle: 'Marker names',
      isIncomplete: true,
      opensMarkerNamingForm: false,
      guidance: 'Finish naming the remaining non-DNA channels to make this cohort ready for normalization.',
    };
  }

  return {
    chipLabel: `${channelCount} ${channelCount === 1 ? 'marker' : 'markers'}`,
    popoverTitle: 'Markers',
    isIncomplete: false,
    opensMarkerNamingForm: false,
    guidance: undefined,
  };
};

// Same fallback the backend uses when matching unnamed channels across slides.
export const getSourceChannelName = (slide: Slide, channelId: string) =>
  trim(slide.channels?.[channelId]?.name || '') || `Channel_${channelId}`;

// Channel order differs across slides, so case rows are merged by source channel name, not channel id.
export const getMergedCaseMultiplexSlide = (slides: Slide[] = [], caseId?: string | number): Slide | null => {
  const multiplexSlides = filter(slides, { stainingType: MULTIPLEX_STAIN_ID });
  if (!multiplexSlides.length) {
    return null;
  }

  if (multiplexSlides.length === 1) {
    return multiplexSlides[0];
  }

  const markerIdBySourceName = new Map<string, string>();
  forEach(multiplexSlides, (slide) => {
    forEach(getMultiplexChannelIds(slide), (channelId) => {
      const sourceName = getSourceChannelName(slide, channelId);
      const markerId = getAssignedMarkerId(slide.channelMarkerTypes?.[channelId]);
      if (!markerIdBySourceName.get(sourceName)) {
        markerIdBySourceName.set(sourceName, markerId || '');
      }
    });
  });
  const sourceNames = Array.from(markerIdBySourceName.keys());

  if (!sourceNames.length) {
    return multiplexSlides[0];
  }

  return {
    id: `case-${caseId ?? 'multiplex'}-markers`,
    stainingType: MULTIPLEX_STAIN_ID,
    channelMarkerTypes: fromPairs(
      map(sourceNames, (sourceName, index) => [String(index), markerIdBySourceName.get(sourceName) || ''])
    ),
    channels: fromPairs(map(sourceNames, (sourceName, index) => [String(index), { name: sourceName }])),
  } as Slide;
};
